/**
 * A etiqueta de qualidade de uma transmissao.
 *
 * O numero que interessa e o que ESTA CHEGANDO em quem assiste, nao o que quem
 * transmite escolheu no menu. Com simulcast os dois divergem de proposito: o
 * publicador manda tres camadas e o SFU entrega a que couber na rede daquela
 * pessoa. Mostrar aqui a escolha do publicador seria dizer "1080p" pra quem
 * esta recebendo 540p — exatamente no momento em que a informacao mais
 * importa, que e quando a imagem piorou e a pessoa quer saber por que.
 */

/**
 * ## De onde vem cada numero
 *
 * Os tres pedacos da etiqueta tem fontes diferentes, e isso e deliberado:
 *
 * - **Resolucao** — do `<video>`, pelo evento `resize`. Custa zero.
 * - **fps** — dos quadros que o `<video>` DESENHOU, contados aqui. Tambem nao
 *   passa por WebRTC, e e por isso que aparece mesmo sozinho numa sala: em
 *   modo mesh, sem ninguem do outro lado nao existe conexao nenhuma, e um fps
 *   tirado de `getStats()` nunca apareceria pra quem esta conferindo a propria
 *   tela — que e exatamente o que a pessoa faz primeiro. De quebra e o numero
 *   mais honesto: quadro desenhado e o que ela ve.
 * - **Bitrate** — de `getStats()`, porque nao ha outro lugar de onde tirar.
 *   Sozinho na sala ele nao existe MESMO: nada esta sendo codificado nem
 *   enviado, e a etiqueta corretamente fica sem ele.
 */

/** Medida bruta de uma trilha, do jeito que `getStats()` a entrega. */
export interface TrackSample {
    /** Bytes acumulados (recebidos ou enviados) desde o inicio da conexao. */
    bytes: number
    /** Quando a medida foi tirada, em ms. */
    timestamp: number
    /**
     * O encoder desta trilha e de hardware? So existe no que EU envio, e so
     * quando o navegador conta (`powerEfficientEncoder` — o Chrome so expoe
     * com captura ativa na pagina; Firefox e Safari, nunca).
     */
    hardware?: boolean
}

/** O que a etiqueta mostra. */
export interface StreamQuality {
    width: number
    height: number
    /** `null` enquanto nao ha duas leituras do `<video>`. */
    fps: number | null
    /** Bits por segundo. `null` quando nao ha conexao de midia pra medir. */
    bitrate: number | null
}

/** Tamanho e cadencia medidos no proprio `<video>`. */
export interface StreamMeasure {
    width: number
    height: number
    fps: number | null
}

/**
 * Bits por segundo entre duas leituras.
 *
 * `getStats()` entrega contadores acumulados, nao taxas: o que vale e o quanto
 * eles andaram entre uma leitura e a seguinte. Devolve `null` quando o relogio
 * nao andou (duas leituras no mesmo instante dariam divisao por zero) ou quando
 * o contador voltou atras — o que acontece de verdade toda vez que a conexao e
 * refeita, e sem esta guarda viraria um pico absurdo na tela.
 */
export function bitrateBetween(previous: TrackSample, current: TrackSample): number | null {
    const seconds = (current.timestamp - previous.timestamp) / 1000
    if (seconds <= 0) return null
    if (current.bytes < previous.bytes) return null
    return Math.round(((current.bytes - previous.bytes) * 8) / seconds)
}

/**
 * Quadros que este `<video>` ja desenhou, ou `null` sem suporte.
 *
 * A diferenca entre duas leituras, dividida pelo tempo, e o fps de verdade —
 * inclusive quando ele cai porque a maquina nao esta dando conta, que e
 * justamente quando alguem olha pra esse numero.
 */
export function renderedFrames(video: HTMLVideoElement): number | null {
    const element = video as HTMLVideoElement & { webkitDecodedFrameCount?: number }
    if (typeof video.getVideoPlaybackQuality === 'function') {
        return video.getVideoPlaybackQuality().totalVideoFrames
    }
    return typeof element.webkitDecodedFrameCount === 'number' ? element.webkitDecodedFrameCount : null
}

/**
 * Altura vira o nome que as pessoas usam.
 *
 * Aproxima pro degrau conhecido mais proximo: uma janela compartilhada quase
 * nunca tem exatamente 1080 px de altura, e "1073p" nao diz nada a ninguem.
 */
const STEPS = [2160, 1440, 1080, 720, 540, 480, 360, 240]

export function heightLabel(height: number): string {
    if (!Number.isFinite(height) || height <= 0) return ''
    // O degrau mais proximo em proporcao, nao em diferenca absoluta: 900 px
    // esta mais perto de 1080 que de 720 em px, mas nao em experiencia.
    let best = STEPS[0]!
    let bestDistance = Infinity
    for (const step of STEPS) {
        const distance = Math.abs(Math.log(height / step))
        if (distance < bestDistance) {
            bestDistance = distance
            best = step
        }
    }
    return `${best}p`
}

/** 4_200_000 vira "4.2 Mbps"; 820_000 vira "820 kbps". */
export function formatBitrate(bitrate: number): string {
    if (!Number.isFinite(bitrate) || bitrate <= 0) return ''
    if (bitrate >= 1_000_000) return `${(bitrate / 1_000_000).toFixed(1)} Mbps`
    return `${Math.round(bitrate / 1000)} kbps`
}

/**
 * Etiqueta completa: `1080p60 · 4.2 Mbps`.
 *
 * Cada pedaco so entra quando existe. Sem resolucao nao ha etiqueta nenhuma —
 * um "· 4.2 Mbps" solto num tile que ainda esta conectando informa menos do que
 * nao mostrar nada.
 */
export function qualityLabel(quality: StreamQuality | null | undefined): string {
    if (!quality) return ''
    // O lado CURTO, que e o que "1080p" sempre quis dizer. Pela altura crua,
    // um video em pe de 608x1080 se anunciava como 1080p — o mesmo rotulo de
    // um 1920x1080, com um terco dos pixels. Ver `docs/orientacao-de-video.md`.
    const size = heightLabel(Math.min(quality.width, quality.height))
    if (!size) return ''

    const fps = quality.fps && quality.fps > 0 ? String(quality.fps) : ''
    const bitrate = quality.bitrate ? formatBitrate(quality.bitrate) : ''
    return bitrate ? `${size}${fps} · ${bitrate}` : `${size}${fps}`
}

/**
 * Le uma conexao e acumula as medidas por id de trilha.
 *
 * O id da trilha e a chave porque e a unica coisa que os dois transportes tem
 * em comum: na malha a trilha chega por uma conexao por par, no SFU por uma
 * assinatura, e dos dois lados o `MediaStreamTrack` entregue a UI e o mesmo
 * objeto cujo `id` aparece aqui. Sem essa ponte, casar uma medida com o tile
 * certo exigiria um mapa paralelo em cada transporte.
 *
 * Acumula em vez de sobrescrever: na malha a MESMA trilha e enviada uma vez
 * para cada espectador, e o numero que interessa a quem transmite e a soma —
 * o quanto esta saindo da maquina dele no total. Vale igual pras camadas de
 * simulcast, que aparecem como varios `outbound-rtp` da mesma fonte.
 *
 * Bytes e, no que sai daqui, se o encoder e de hardware. A cadencia vem do
 * `<video>`, que a sabe sem depender de existir conexao nenhuma.
 */
export async function sampleConnection(
    pc: RTCPeerConnection,
    into: Map<string, TrackSample>,
    now: number
): Promise<void> {
    if (typeof pc.getStats !== 'function') return

    let report: RTCStatsReport
    try {
        report = await pc.getStats()
    } catch {
        // Conexao fechando no meio da leitura: a proxima rodada pega.
        return
    }

    // `outbound-rtp` nao carrega o id da trilha; quem carrega e o
    // `media-source` que ele aponta. Este primeiro passo monta a ponte.
    const sources = new Map<string, string>()
    report.forEach((stat) => {
        const item = stat as { type?: string; id?: string; trackIdentifier?: string }
        if (item.type === 'media-source' && item.id && item.trackIdentifier) {
            sources.set(item.id, item.trackIdentifier)
        }
    })

    report.forEach((stat) => {
        const item = stat as {
            type?: string
            kind?: string
            trackIdentifier?: string
            mediaSourceId?: string
            bytesReceived?: number
            bytesSent?: number
            powerEfficientEncoder?: boolean
        }
        if (item.kind !== 'video') return

        let trackId: string | undefined
        let bytes = 0
        let hardware: boolean | undefined

        if (item.type === 'inbound-rtp') {
            trackId = item.trackIdentifier
            bytes = item.bytesReceived ?? 0
        } else if (item.type === 'outbound-rtp') {
            trackId = item.mediaSourceId ? sources.get(item.mediaSourceId) : undefined
            bytes = item.bytesSent ?? 0
            hardware = item.powerEfficientEncoder
        } else {
            return
        }
        if (!trackId) return

        const before = into.get(trackId)
        // Uma conexao em software ja basta pra dizer que a GPU nao deu conta
        // de tudo: na malha, com um encoder por espectador, a placa pode
        // esgotar as sessoes dela e o navegador mandar o resto pro processador.
        const combined = before?.hardware === undefined
            ? hardware
            : hardware === undefined ? before.hardware : before.hardware && hardware
        into.set(trackId, {
            bytes: (before?.bytes ?? 0) + bytes,
            timestamp: now,
            ...(combined === undefined ? {} : { hardware: combined })
        })
    })
}
